import { SessionMode, LLMProvider } from '../types';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4005';
const HUB_TOKEN = process.env.REACT_APP_HUB_TOKEN || '';

const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export interface InitSessionResult {
  callUUID: string;
  wsUrl: string;
}

export async function fetchSessionToken(
  sessionId: string,
  mode: SessionMode
): Promise<string> {
  const sessionTypeMap: Record<SessionMode, number> = {
    audio: 0,
    text: 1,
    audio_and_text: 2,
  };

  const resp = await fetch(`${API_BASE}/api/getSession`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${HUB_TOKEN}`,
    },
    body: JSON.stringify({ data: { sessionID: sessionId, sessionType: sessionTypeMap[mode] } }),
  });

  if (!resp.ok) {
    const err = await resp.json();
    throw new Error(err.message || 'Failed to get session token');
  }

  const data = await resp.json();
  return data.token as string;
}

export async function initSession(
  token: string,
  assistantId: string,
  assistantVersion: string,
  mode: SessionMode,
  provider: LLMProvider
): Promise<InitSessionResult> {
  const resp = await fetch(`${API_BASE}/api/init`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      mode,
      provider,
      assistant: { id: assistantId, version: assistantVersion },
    }),
  });

  if (!resp.ok) {
    const err = await resp.json();
    throw new Error(err.message || 'Failed to initialize session');
  }

  return resp.json() as Promise<InitSessionResult>;
}

export async function disconnectSession(callUUID: string): Promise<void> {
  await fetch(`${API_BASE}/api/disconnect`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ callUUID }),
  });
}

// ── Twilio Dashboard API ───────────────────────────────────────────────────────

export interface TwilioScript {
  id: string;
  name: string;
  systemPrompt: string;
  context: string;
  greetingMessage: string;
  voice: string;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ConversationSummary {
  callSid: string;
  from: string;
  startTime: string;
  scriptName: string;
  durationSeconds?: number;
  summary?: string;
  campaignName?: string;
  aiStatusJson?: string;
  followupDateTime?: string | null;
  ptpDate?: string | null;
  sendPaymentLink?: number;
  riskCategory?: string;
  aiStatusSuggestion?: string | null;
  customerName?: string;
  recordingUrl?: string | null;
}

export interface PaginatedConversations {
  data: ConversationSummary[];
  total: number;
  page: number;
  limit: number;
}

export interface ConversationTurn {
  role: 'agent' | 'customer';
  text: string;
  timestamp: string;
}

export interface ConversationDetail {
  callSid: string;
  from: string;
  to: string;
  scriptId: string;
  scriptName: string;
  startTime: string;
  endTime?: string;
  durationSeconds?: number;
  turns: ConversationTurn[];
  summary?: string;
  aiStatusJson?: string;
  followupDateTime?: string | null;
  ptpDate?: string | null;
  sendPaymentLink?: number;
  riskCategory?: string;
  aiStatusSuggestion?: string | null;
  customerName?: string;
  recordingUrl?: string | null;
}

export interface TwilioConfig {
  webhookUrl: string;
  streamUrl: string;
  twilioNumber: string;
  accountSid: string;
  googleModel: string;
}

async function twilioFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}/api/twilio${path}`, {
    headers: { 
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    ...options,
  });
  if (res.status === 401 || res.status === 403) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.dispatchEvent(new Event('unauthorized'));
    throw new Error('Session expired');
  }
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  if (res.status === 204) return undefined as unknown as T;
  return res.json() as Promise<T>;
}

export const fetchScripts = () =>
  twilioFetch<TwilioScript[]>('/scripts');

export const createScript = (data: Omit<TwilioScript, 'id' | 'createdAt' | 'updatedAt'>) =>
  twilioFetch<TwilioScript>('/scripts', { method: 'POST', body: JSON.stringify(data) });

export const updateScript = (id: string, data: Partial<TwilioScript>) =>
  twilioFetch<TwilioScript>(`/scripts/${id}`, { method: 'PUT', body: JSON.stringify(data) });

export const deleteScript = (id: string) =>
  twilioFetch<void>(`/scripts/${id}`, { method: 'DELETE' });

export const fetchConversations = () =>
  twilioFetch<ConversationSummary[]>('/conversations');

export const fetchConversation = (callSid: string) =>
  twilioFetch<ConversationDetail>(`/conversations/${callSid}`);

export const fetchTwilioConfig = () =>
  twilioFetch<TwilioConfig>('/config');

export interface OutboundCallResult {
  callSid: string;
  message: string;
}

export const makeOutboundCall = (to: string, scriptId?: string) =>
  twilioFetch<OutboundCallResult>('/call', {
    method: 'POST',
    body: JSON.stringify({ to, scriptId }),
  });

// ── Exotel Dashboard API ───────────────────────────────────────────────────────

export interface ExotelConfig {
  webhookUrl: string;
  streamUrl: string;
  callerID: string;
  accountSid: string;
  googleModel: string;
}

async function exotelFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}/api/exotel${path}`, {
    headers: { 
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    ...options,
  });
  if (res.status === 401 || res.status === 403) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.dispatchEvent(new Event('unauthorized'));
    throw new Error('Session expired');
  }
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  if (res.status === 204) return undefined as unknown as T;
  return res.json() as Promise<T>;
}

export const fetchExotelScripts = () =>
  exotelFetch<TwilioScript[]>('/scripts');

export const createExotelScript = (data: Omit<TwilioScript, 'id' | 'createdAt' | 'updatedAt'>) =>
  exotelFetch<TwilioScript>('/scripts', { method: 'POST', body: JSON.stringify(data) });

export const updateExotelScript = (id: string, data: Partial<TwilioScript>) =>
  exotelFetch<TwilioScript>(`/scripts/${id}`, { method: 'PUT', body: JSON.stringify(data) });

export const deleteExotelScript = (id: string) =>
  exotelFetch<void>(`/scripts/${id}`, { method: 'DELETE' });

export const fetchExotelConversations = (
  page?: number,
  limit?: number | string,
  phone?: string,
  campaign?: string,
  fromDate?: string,
  toDate?: string
) => {
  const params = new URLSearchParams();
  if (page) params.append('page', page.toString());
  if (limit) params.append('limit', limit.toString());
  if (phone) params.append('phone', phone);
  if (campaign) params.append('campaign', campaign);
  if (fromDate) params.append('fromDate', fromDate);
  if (toDate) params.append('toDate', toDate);

  const queryString = params.toString();
  const path = queryString ? `/conversations?${queryString}` : '/conversations';
  return exotelFetch<any>(path);
};

export const fetchExotelConversation = (callSid: string) =>
  exotelFetch<ConversationDetail>(`/conversations/${callSid}`);

export const reanalyzeExotelConversation = (callSid: string) =>
  exotelFetch<ConversationDetail>(`/conversations/${callSid}/reanalyze`, { method: 'POST' });

export const fetchExotelConfig = () =>
  exotelFetch<ExotelConfig>('/config');

export const makeExotelCall = (to: string, scriptId?: string) =>
  exotelFetch<OutboundCallResult>('/call', {
    method: 'POST',
    body: JSON.stringify({ to, scriptId }),
  });

export interface Agent {
  id: string;
  name: string;
  exotelNumber: string | null;
  llmModel: string;
  scriptId: string | null;
  scriptName?: string;
  createdAt?: string;
  updatedAt?: string;
}

export const fetchExotelAgents = () =>
  exotelFetch<Agent[]>('/agents');

export const createExotelAgent = (data: Omit<Agent, 'id' | 'scriptName' | 'createdAt' | 'updatedAt'>) =>
  exotelFetch<Agent>('/agents', { method: 'POST', body: JSON.stringify(data) });

export const updateExotelAgent = (id: string, data: Partial<Agent>) =>
  exotelFetch<Agent>(`/agents/${id}`, { method: 'PUT', body: JSON.stringify(data) });

export const deleteExotelAgent = (id: string) =>
  exotelFetch<void>(`/agents/${id}`, { method: 'DELETE' });

// ── User Management & Auth API ─────────────────────────────────────────────────

export interface User {
  id: number;
  username: string;
  role: 'super_admin' | 'admin' | 'customer';
  createdAt?: string;
}

async function authFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}/api/auth${path}`, {
    headers: { 
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    ...options,
  });
  if (path !== '/login' && (res.status === 401 || res.status === 403)) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.dispatchEvent(new Event('unauthorized'));
    throw new Error('Session expired');
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Request failed: ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export const loginUser = (username: string, password: string) =>
  authFetch<{ token: string; user: User }>('/login', {
    method: 'POST',
    body: JSON.stringify({ username, password })
  });

export const resetPassword = (oldPassword: string, newPassword: string) =>
  authFetch<{ message: string }>('/reset-password', {
    method: 'POST',
    body: JSON.stringify({ oldPassword, newPassword })
  });

export const fetchUsers = () =>
  authFetch<User[]>('/users');

export const createUser = (data: Omit<User, 'id' | 'createdAt'> & { password: string }) =>
  authFetch<{ message: string }>('/users', {
    method: 'POST',
    body: JSON.stringify(data)
  });

// ── Campaigns API ─────────────────────────────────────────────────────────────

export interface Campaign {
  id: string;
  name: string;
  agentId: string;
  agentName?: string;
  concurrency?: number;
  status: 'pending' | 'calling' | 'paused' | 'completed' | 'failed';
  createdAt: string;
  totalCalls: number;
  pendingCalls: number;
  completedCalls: number;
  failedCalls: number;
}

export interface CampaignCall {
  id: number;
  campaignId: string;
  phone: string;
  name: string | null;
  status: 'pending' | 'calling' | 'completed' | 'failed' | 'busy' | 'no-answer';
  callSid: string | null;
  durationSeconds: number;
  summary: string | null;
  variables?: string | Record<string, string> | null;
  createdAt: string;
}

async function campaignFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}/api/campaigns${path}`, {
    headers: { 
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    ...options,
  });
  if (res.status === 401 || res.status === 403) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.dispatchEvent(new Event('unauthorized'));
    throw new Error('Session expired');
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Request failed: ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export const uploadCampaign = (name: string, agentId: string, contacts: { phone: string; name?: string }[], concurrency?: number) =>
  campaignFetch<Campaign>('/upload', {
    method: 'POST',
    body: JSON.stringify({ name, agentId, contacts, concurrency })
  });

export const updateCampaign = (id: string, name: string, agentId: string, contacts: { phone: string; name?: string }[], concurrency?: number) =>
  campaignFetch<Campaign>(`/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ name, agentId, contacts, concurrency })
  });

export const fetchCampaigns = () =>
  campaignFetch<Campaign[]>('/');

export const fetchCampaignCalls = (id: string) =>
  campaignFetch<CampaignCall[]>(`/${id}/calls`);

export const startCampaign = (id: string) =>
  campaignFetch<{ message: string }>(`/${id}/start`, { method: 'POST' });

export const pauseCampaign = (id: string) =>
  campaignFetch<{ message: string }>(`/${id}/pause`, { method: 'POST' });

export const resumeCampaign = (id: string) =>
  campaignFetch<{ message: string }>(`/${id}/resume`, { method: 'POST' });

export const restartCampaign = (id: string) =>
  campaignFetch<{ message: string }>(`/${id}/restart`, { method: 'POST' });

// ── Token Consumption API ──────────────────────────────────────────────────────

export interface TokenConsumptionApiWise {
  apiName: string;
  modelName: string;
  totalPromptTokens: number;
  totalCompletionTokens: number;
  totalTokens: number;
  totalCost: number;
  requestCount: number;
}

export interface TokenConsumptionDateWise {
  date: string;
  totalPromptTokens: number;
  totalCompletionTokens: number;
  totalTokens: number;
  totalCost: number;
  requestCount: number;
}

export interface TokenConsumptionDayWise {
  dayName: string;
  dayOfWeek: number;
  totalPromptTokens: number;
  totalCompletionTokens: number;
  totalTokens: number;
  totalCost: number;
  requestCount: number;
}

export interface TokenConsumptionData {
  apiWise: TokenConsumptionApiWise[];
  dateWise: TokenConsumptionDateWise[];
  dayWise: TokenConsumptionDayWise[];
  campaigns: string[];
  usdToInrRate: number;
}

export async function fetchTokenConsumption(startDate?: string, endDate?: string, campaignName?: string): Promise<TokenConsumptionData> {
  let url = `${API_BASE}/api/token-consumption`;
  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  if (campaignName) params.append('campaignName', campaignName);

  const queryString = params.toString();
  if (queryString) {
    url += `?${queryString}`;
  }

  const resp = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
  });

  if (resp.status === 401 || resp.status === 403) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.dispatchEvent(new Event('unauthorized'));
    throw new Error('Session expired');
  }

  if (!resp.ok) {
    throw new Error(`Request failed: ${resp.status}`);
  }

  return resp.json() as Promise<TokenConsumptionData>;
}

// ── Model Pricing Configuration APIs ───────────────────────────────────────────

export interface ModelPricing {
  modelName: string;
  inputPricePerMillion: number;
  outputPricePerMillion: number;
  createdAt?: string;
  updatedAt?: string;
}

export async function fetchModelPricing(): Promise<ModelPricing[]> {
  const resp = await fetch(`${API_BASE}/api/token-consumption/pricing`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
  });

  if (resp.status === 401 || resp.status === 403) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.dispatchEvent(new Event('unauthorized'));
    throw new Error('Session expired');
  }

  if (!resp.ok) {
    throw new Error(`Request failed: ${resp.status}`);
  }

  return resp.json() as Promise<ModelPricing[]>;
}

export async function saveModelPricing(data: ModelPricing): Promise<void> {
  const resp = await fetch(`${API_BASE}/api/token-consumption/pricing`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(data),
  });

  if (resp.status === 401 || resp.status === 403) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.dispatchEvent(new Event('unauthorized'));
    throw new Error('Session expired');
  }

  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.error || `Request failed: ${resp.status}`);
  }
}

export async function deleteModelPricing(modelName: string): Promise<void> {
  const resp = await fetch(`${API_BASE}/api/token-consumption/pricing/${encodeURIComponent(modelName)}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
  });

  if (resp.status === 401 || resp.status === 403) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.dispatchEvent(new Event('unauthorized'));
    throw new Error('Session expired');
  }

  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.error || `Request failed: ${resp.status}`);
  }
}